// Shared widgets for the settings page (spec 21 §2.3 attr-type → widget map).
// Pure presentational + the confirm-dialog flow (single / double confirm,
// spec 21 §2.3: 雙重確認 = dialog + 輸入裝置名稱二段).

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttrHandle } from './ops';
import { useFocusTrap } from '../../../lib/useFocusTrap';

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--spacing-6)',
  padding: 'var(--spacing-4) 0',
  borderBottom: '1px solid var(--color-surface-line)',
};

export function SectionCard({
  title,
  children,
  footer,
  action,
}: {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="card" style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
      {action ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-6)' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>{title}</h2>
          {action}
        </div>
      ) : (
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>{title}</h2>
      )}
      {children}
      {footer}
    </section>
  );
}

export function RowShell({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div style={rowStyle}>
      <div style={{ minWidth: 0 }}>
        <div>{label}</div>
        {hint && (
          <div className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
            {hint}
          </div>
        )}
      </div>
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>{children}</div>
    </div>
  );
}

function AttrStateNote({ attr }: { attr: AttrHandle }) {
  const { t } = useTranslation();
  if (attr.status === 'loading') return <span className="text-secondary">{t('deviceSettings.reading')}</span>;
  if (attr.status === 'unavailable')
    return (
      <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
        {t('deviceSettings.cannotRead')}
      </span>
    );
  return null;
}

/** ATTR SET bool → toggle (optimistic + rollback handled by useAttr). */
export function ToggleRow({ label, hint, attr, disabled, onAfterOn }: { label: string; hint?: string; attr: AttrHandle; disabled?: boolean; onAfterOn?: () => void }) {
  if (attr.status === 'unavailable') {
    return (
      <RowShell label={label} hint={hint}>
        <AttrStateNote attr={attr} />
      </RowShell>
    );
  }
  const on = attr.value === true || attr.value === 1;
  return (
    <RowShell label={label} hint={attr.setError ?? hint}>
      <AttrStateNote attr={attr} />
      <button
        role="switch"
        aria-checked={on}
        aria-label={label}
        disabled={disabled || attr.status !== 'ready' || attr.setting}
        onClick={() => {
          const next = !on;
          attr.set(next);
          if (next && onAfterOn) onAfterOn();
        }}
        style={{
          width: 44,
          height: 26,
          borderRadius: 'var(--radius-full)',
          border: '1px solid var(--color-surface-line)',
          background: on ? 'var(--color-brand-primary)' : 'var(--color-surface-panel2)',
          position: 'relative',
          transition: 'background var(--motion-duration-fast) var(--motion-easing-ease)',
          opacity: disabled || attr.status !== 'ready' ? 'var(--opacity-disabled)' : 1,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: on ? 20 : 2,
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: 'var(--color-text-inverse, #fff)',
            backgroundColor: '#fff',
            transition: 'left var(--motion-duration-fast) var(--motion-easing-ease)',
          }}
        />
      </button>
    </RowShell>
  );
}

/** ATTR SET enum → segmented control. options: [value, label][] */
export function SegmentedRow({ label, hint, attr, options, disabled }: { label: string; hint?: string; attr: AttrHandle; options: Array<[number, string]>; disabled?: boolean }) {
  if (attr.status === 'unavailable') return null; // optional member probe failed → hide (spec 21 §2.1)
  return (
    <RowShell label={label} hint={attr.setError ?? hint}>
      <AttrStateNote attr={attr} />
      <div style={{ display: 'flex', border: '1px solid var(--color-surface-line)', borderRadius: 'var(--radius-full)', overflow: 'hidden' }}>
        {options.map(([v, text]) => {
          const active = attr.value === v;
          return (
            <button
              key={v}
              disabled={disabled || attr.status !== 'ready' || attr.setting}
              onClick={() => attr.set(v)}
              style={{
                border: 'none',
                padding: 'var(--spacing-2) var(--spacing-5)',
                fontSize: 'var(--font-size-sm)',
                background: active ? 'var(--color-brand-primary)' : 'transparent',
                color: active ? 'var(--color-text-on-brand)' : 'var(--color-text-secondary)',
              }}
            >
              {text}
            </button>
          );
        })}
      </div>
    </RowShell>
  );
}

/** ATTR SET int (bounded) → slider，鬆手才送（spec 21 §2.3：僅送最終值）。 */
export function SliderRow({ label, hint, attr, min, max, unit, disabled, scale = 1 }: { label: string; hint?: string; attr: AttrHandle; min: number; max: number; unit?: string; disabled?: boolean; /** wire 值 = 顯示值 × scale（0.01/LSB 傳 100） */ scale?: number }) {
  const [local, setLocal] = useState<number | null>(null);
  if (attr.status === 'unavailable') return null; // optional member → hide
  const current = local ?? (typeof attr.value === 'number' ? Math.round(attr.value / scale) : min);
  return (
    <RowShell label={label} hint={attr.setError ?? hint}>
      <AttrStateNote attr={attr} />
      <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', minWidth: 48, textAlign: 'right' }}>
        {current}
        {unit ?? ''}
      </span>
      <input
        aria-label={label}
        type="range"
        min={min}
        max={max}
        value={current}
        disabled={disabled || attr.status !== 'ready' || attr.setting}
        onChange={(e) => setLocal(Number(e.target.value))}
        onMouseUp={() => {
          if (local !== null) attr.set(local * scale);
          setLocal(null);
        }}
        onTouchEnd={() => {
          if (local !== null) attr.set(local * scale);
          setLocal(null);
        }}
        style={{ width: 140, minHeight: 'auto', padding: 0, border: 'none', background: 'transparent' }}
      />
    </RowShell>
  );
}

/** ATTR STAT → readonly row（值 + 手動重整）。 */
export function ReadonlyRow({ label, attr, render }: { label: string; attr: AttrHandle; render?: (v: NonNullable<AttrHandle['value']>) => ReactNode }) {
  const { t } = useTranslation();
  return (
    <RowShell label={label}>
      {attr.status === 'loading' && <span className="text-secondary">{t('deviceSettings.reading')}</span>}
      {attr.status === 'unavailable' && (
        <>
          <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
            {t('deviceSettings.cannotRead')}
          </span>
          <button className="btn-ghost" style={{ padding: 'var(--spacing-1) var(--spacing-4)', fontSize: 'var(--font-size-sm)' }} onClick={attr.refresh}>
            {t('common.retry')}
          </button>
        </>
      )}
      {attr.status === 'ready' && <span>{attr.value !== undefined ? (render ? render(attr.value) : String(attr.value)) : '—'}</span>}
    </RowShell>
  );
}

/** advertise-only sub chips（spec 21 §2.3 EVENT 成員：不渲染 toggle）。 */
export function Chips({ label, subs, labels }: { label: string; subs: string[]; labels: Record<string, string> }) {
  if (subs.length === 0) return null;
  return (
    <RowShell label={label}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
        {subs.map((s) => (
          <span
            key={s}
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-surface-line)',
              borderRadius: 'var(--radius-full)',
              padding: '2px var(--spacing-4)',
            }}
          >
            {labels[s.toLowerCase()] ?? labels[s] ?? s}
          </span>
        ))}
      </div>
    </RowShell>
  );
}

// ---- confirm dialog (single + double confirm flows) ----

export interface ConfirmSpec {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  /** Optional cancel-button label (defaults to common.cancel). Use when the cancel path performs
   *  a real alternative action rather than a no-op (e.g. R4 "Stop recording"). */
  cancelLabel?: string;
  danger?: boolean;
  /** Double-confirm: user must type this exact string (e.g. device name) to enable confirm. */
  requireText?: string;
  requireTextHint?: string;
}

export function ConfirmDialog({ spec, busy, onConfirm, onCancel }: { spec: ConfirmSpec; busy?: boolean; onConfirm: () => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  useEffect(() => setTyped(''), [spec]);
  const blocked = spec.requireText !== undefined && typed !== spec.requireText;
  const dialogRef = useRef<HTMLDivElement>(null);
  // a11y: trap focus inside the dialog + restore on close; Esc cancels (unless busy).
  useFocusTrap(true, dialogRef);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);
  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={spec.title}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 1000,
        padding: 'var(--spacing-8)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="card" style={{ width: 'min(420px, 100%)', display: 'grid', gap: 'var(--spacing-5)' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>{spec.title}</h3>
        <div className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
          {spec.body}
        </div>
        {spec.requireText !== undefined && (
          <label style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
            <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
              {spec.requireTextHint ?? t('deviceSettings.confirmTypeHint', { text: spec.requireText })}
            </span>
            <input data-autofocus value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={spec.requireText} />
          </label>
        )}
        <div style={{ display: 'flex', gap: 'var(--spacing-4)', justifyContent: 'flex-end' }}>
          <button className="btn-ghost" data-autofocus disabled={busy} onClick={onCancel}>
            {spec.cancelLabel ?? t('common.cancel')}
          </button>
          <button className={spec.danger ? 'btn-danger' : 'btn-primary'} disabled={busy || blocked} onClick={onConfirm}>
            {busy ? t('common.running') : spec.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
